<script lang="ts" setup>
defineOptions({
  inheritAttrs: false,
});

const {
  minimumOrderQuantity,
  maximumOrderQuantity,
  incrementalOrderQuantity,
  requestedOrderQuantity,
} = defineProps<{
  minimumOrderQuantity: number;
  maximumOrderQuantity?: number | null;
  incrementalOrderQuantity: number;
  requestedOrderQuantity?: number | null;
}>();

const { t } = useI18n();

const model = defineModel({ default: 0 });
const moq = ref(minimumOrderQuantity);
const ioq = ref(incrementalOrderQuantity);

defineExpose({
  updateProps: (minimumOrderQuantity: number, incrementalOrderQuantity: number) => {
    moq.value = minimumOrderQuantity;
    ioq.value = incrementalOrderQuantity;
    updateOrderQuantity(); // validation of current quantity
  },
});

const heightClass = "h-[36px] md:h-[40.5px]";
const sizeClass = "size-[36px] md:size-[40.5px]";

const inputUi = {
  root: "w-full",
  base: `bg-white border-solid border-neutral-100 p-0 focus:border-non focus:outline-none focus:ring-0 focus:shadow-none ${heightClass}`,
};

const btnUi = {
  base: `bg-[#F3F1EC] active:bg-[#F3F1EC] disabled:bg-[#F3F1EC] hover:bg-[#F3F1EC] border-solid border-neutral-100 text-neutral text-sm rounded-md ${sizeClass}`,
  leadingIcon: "w-4 h-4",
};

function decrementOrderQuantity() {
  let orderQuantity = model.value;

  orderQuantity -= ioq.value;
  orderQuantity = normalizeOrderQuantity(orderQuantity, ioq.value);

  if (orderQuantity < moq.value) {
    orderQuantity = moq.value;
    useInfoToast(t("common.title.info"), t("product.message.warning.minimum-order-quantity"));
  }

  model.value = orderQuantity;
}

function incrementOrderQuantity() {
  let orderQuantity = model.value;

  orderQuantity += ioq.value;
  orderQuantity = normalizeOrderQuantity(orderQuantity, ioq.value);

  if ((maximumOrderQuantity || maximumOrderQuantity === 0) && orderQuantity > maximumOrderQuantity) {
    orderQuantity = maximumOrderQuantity;
    useInfoToast(t("common.title.info"), t("product.message.warning.maximum-order-quantity"));
  }

  model.value = orderQuantity;
}

function updateOrderQuantity() {
  let orderQuantity = model.value;

  if (typeof orderQuantity !== "number") {
    orderQuantity = requestedOrderQuantity ?? moq.value;
  }

  orderQuantity = normalizeOrderQuantity(orderQuantity, ioq.value);

  if (orderQuantity < moq.value) {
    orderQuantity = moq.value;
    useInfoToast(t("common.title.info"), t("product.message.warning.minimum-order-quantity"));
  }

  if ((maximumOrderQuantity || maximumOrderQuantity === 0) && orderQuantity > maximumOrderQuantity) {
    orderQuantity = maximumOrderQuantity;
    useInfoToast(t("common.title.info"), t("product.message.warning.maximum-order-quantity"));
  }

  model.value = orderQuantity;
}

function normalizeOrderQuantity(orderQuantity: number, incrementalOrderQuantity: number): number {
  const divideValue = orderQuantity / incrementalOrderQuantity;
  const divideRounded = Math.floor(divideValue);
  return divideRounded * incrementalOrderQuantity;
}

onMounted(() => {
  model.value = requestedOrderQuantity ?? moq.value;
  updateOrderQuantity(); // validation of initial quantity
});
</script>

<template>
  <UFieldGroup>
    <UButton
      variant="solid" color="neutral" size="md" icon="i-ph-minus" square data-testid="decrement-button"
      :ui="btnUi as any"
      style="border-width: 1px; border-right-width: 0;"
      @click="decrementOrderQuantity"
    />

    <UInput
      v-model.number="model" v-bind="$attrs"
      variant="none" size="md"
      data-1p-ignore required
      data-testid="order-quantity"
      :ui="inputUi as any"
      style="border-width: 1px; border-left-width: 0; border-right-width: 0; text-align: center;"
      @change="updateOrderQuantity"
    />

    <UButton
      variant="solid" color="neutral" size="md" icon="i-ph-plus" square data-testid="increment-button"
      :ui="btnUi as any"
      style="border-width: 1px; border-left-width: 0;"
      @click="incrementOrderQuantity"
    />
  </UFieldGroup>
</template>

<style scoped>

</style>
